<script setup lang="ts">
import VBlockLoader from "@magnit/core/src/components/VLoader/VBlockLoader.vue";
import VText from "@magnit/core/src/components/VText/VText.vue";
import VMarkedText from "@magnit/core/src/components/VMarkedText/VMarkedText.vue";
import VCatalogListing from "@magnit/unit-catalog/src/components/VCatalogListing.vue";
import VDisclaimer from "@magnit/core/src/containers/VDisclaimer/VDisclaimer.vue";
import { sanitizeString } from "~/utils/string";

const route = useRoute();
const storesStore = useStoresStore();
const { requestPromotionsItem } = promoApi();
const { requestSelections } = promoloaderApi();
const promoId = computed(() => String(route.params.id));
const { data: promo, status } = await requestPromotionsItem(promoId);
const { data: selections, pending: pendingSelections } =
  await requestSelections(promoId);
if (status.value === "error" || (status.value === "success" && !promo.value)) {
  useCreateError("Promo not found", HttpCodes.NotFound);
}

const description = computed(() =>
  promo.value?.description ? sanitizeString(promo.value?.description) : "",
);

const seoTitle = computed(
  () =>
    `${promo.value?.name || ""} — подборка товаров сети Магнит, купить в городе ${storesStore.currentStoreCity}`,
);
const seoDescription = computed(
  () => "Покупайте товары из подборки в магазинах Магнит",
);
const seoKeywords = computed(
  () => "Магнит, акции, скидки, каталог товаров, подборка товаров",
);

useSeoMeta({
  title: seoTitle,
  ogTitle: seoTitle,
  description: seoDescription,
  ogDescription: seoDescription,
  keywords: seoKeywords,
});
</script>

<template>
  <div class="popular-detail">
    <VBlockLoader :show="status === 'pending'" absolute />
    <AppPopularTopNav
      v-if="promo"
      :promo-product="promo"
      class="popular-detail__top-nav"
    />
    <VWrapperLocal>
      <div
        v-if="promo"
        class="popular-detail__container"
        itemscope
        itemtype="http://schema.org/Article"
      >
        <meta v-if="promo.name" itemprop="name" :content="promo.name">
        <meta v-if="promo.name" itemprop="headline" :content="promo.name">
        <VText
          v-if="promo.name"
          tag="h1"
          :font="{ xs: 'headline-medium', l: 'display-medium' }"
          class="popular-detail__title"
        >
          {{ promo.name }}
        </VText>
        <AppArticleDueDate :product="promo" class="popular-detail__date" />
        <div
          class="popular-detail__content"
          :class="{ 'popular-detail__content_column': !!promo.imageWebUrl }"
        >
          <div
            v-if="promo.imageWebUrl || promo.imageUrl"
            class="popular-detail__image-wrap"
          >
            <meta
              itemprop="image"
              :content="promo.imageWebUrl || promo.imageUrl"
            >
            <div class="popular-detail__image-wrap__inner">
              <AppImage
                :src="promo.imageWebUrl || promo.imageUrl"
                :alt="promo.name"
                class="popular-detail__image"
              />
              <VDisclaimer v-if="promo.eridInfo" :content="promo.eridInfo" />
            </div>
          </div>
          <meta itemprop="articleBody" :content="description">
          <VMarkedText
            :text="description"
            itemprop="description"
            class="popular-detail__description"
          />
        </div>
        <VCatalogListing
          v-if="pendingSelections || selections?.length"
          :analytics-payload="{
            page: 'popular',
            block: 'goods',
            element: 'item',
            collection_name: promo.name,
            product_type: 19,
          }"
          :loading="pendingSelections"
          :loading-items-count="6"
          :items="selections || []"
          class="popular-detail__goods-list"
        />
      </div>
    </VWrapperLocal>
  </div>
</template>

<style scoped lang="postcss">
.popular-detail {
  &__container {
    @media (--pl-viewport-from-xl) {
      display: flex;
      flex-direction: column;
      gap: var(--pl-unit-x5);
    }
  }

  &__title {
    padding-top: var(--pl-unit-x4);
  }

  &__date {
    padding-top: var(--pl-unit-x2);
  }

  &__content {
    @media (--pl-viewport-from-ml) {
      display: flex;
      gap: var(--pl-unit-x6);
    }

    &_column {
      flex-direction: column;
      gap: 0;
    }
  }

  &__image-wrap {
    position: relative;
    padding: var(--pl-unit-x4) 0;

    &__inner {
      position: relative;
    }
  }

  :deep(.popular-detail__image) {
    display: block;
    margin: auto;
    min-width: 286px;
    max-width: 100%;
    max-height: 460px;
    border-radius: var(--pl-badge-radius-m);
  }

  &__content_column :deep(.popular-detail__image) {
    object-fit: cover;
    width: 100%;
    height: 100%;
  }

  &__description {
    padding: var(--pl-unit-x6) 0;

    @media (--pl-viewport-from-m) {
      padding: var(--pl-unit-x8) 0;
    }
  }

  &__goods-list {
    padding-top: var(--pl-unit-x4);
  }
}
</style>
